import React, { useMemo } from 'react';
import { useDropzone } from 'react-dropzone';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const baseStyle = {
  borderWidth: 2,
  borderRadius: 2,
  borderColor: 'var(--muted)',
  borderStyle: 'dashed',
  backgroundColor: 'var(--background)',
  color: 'var(--muted-foreground)',
  outline: 'none',
  transition: 'border .24s ease-in-out',
  opacity: 1
};

const focusedStyle = {
  borderColor: 'var(--muted-foreground)',
  opacity: 1
};

const acceptStyle = {
  borderColor: 'green',
  opacity: 1
};

const rejectStyle = {
  borderColor: '#ff1744',
  opacity: 1
};

//Converts to bytes(in binary) not decimal
const convertToBytes = (size = 0, abbreviation) => {
  const multipliers = {
    'B': 1,
    'KB': 1024,
    'MB': 1024 * 1024,
    'GB': 1024 * 1024 * 1024,
    'TB': 1024 * 1024 * 1024 * 1024,
    'PB': 1024 * 1024 * 1024 * 1024 * 1024,
    'EB': 1024 * 1024 * 1024 * 1024 * 1024 * 1024,
    'ZB': 1024 * 1024 * 1024 * 1024 * 1024 * 1024 * 1024,
    'YB': 1024 * 1024 * 1024 * 1024 * 1024 * 1024 * 1024 * 1024
  };

  const multiplier = multipliers[abbreviation.toUpperCase()];
  if (!multiplier) {
    return 0;
  }

  return size * multiplier;
};

//Extracts all formats from acceptedFileTypes
const extractFormats = (fileTypes) => {
  const allFormats = [];
  for (const formats of Object.values(fileTypes)) {
    allFormats.push(...formats);
  }
  return [...new Set(allFormats)];
};

const FileDropZone = ({
  isLoading,
  setFiles,
  acceptedFileTypes,
  maxFileSize = 50,
  fileSizeUnit = 'KB',
  title = 'Upload Files',
  supportedFormatsDescription,
  maxFiles = 0,
  usePreviousState = true,
  ...rest
}) => {
  const maxSize = convertToBytes(maxFileSize, fileSizeUnit);
  const formats = extractFormats(acceptedFileTypes)?.join(', ');

  const { getRootProps, getInputProps, isFocused, isDragAccept, isDragReject } = useDropzone({
    accept: { ...acceptedFileTypes },
    maxSize,
    maxFiles,
    onDrop: (acceptedFiles) => {
      setFiles((prev) => {
        if (usePreviousState) {
          return prev
            .filter((item) =>
              acceptedFiles.length > 0 ? acceptedFiles?.some((file) => file.name !== item.name) : true
            )
            .concat(
              acceptedFiles.map((file) =>
                Object.assign(
                  {
                    name: file.name,
                    path: file.path,
                    size: file.size,
                    type: file.type,
                    lastModified: file.lastModified
                  },
                  {
                    preview: URL.createObjectURL(file),
                    file
                  }
                )
              )
            );
        } else {
          // If not using previous state, simply return the new files
          return acceptedFiles.map((file) =>
            Object.assign(
              {
                name: file.name,
                path: file.path,
                size: file.size,
                type: file.type,
                lastModified: file.lastModified
              },
              {
                preview: URL.createObjectURL(file),
                file
              }
            )
          );
        }
      });
    },
    ...rest
  });

  const style = useMemo(
    () => ({
      ...baseStyle,
      ...(isFocused ? focusedStyle : {}),
      ...(isDragAccept ? acceptStyle : {}),
      ...(isDragReject ? rejectStyle : {})
    }),
    [isFocused, isDragAccept, isDragReject]
  );

  return (
    <>
      <div
        {...getRootProps({
          style,
          disabled: isLoading,
          className: `dropzone h-48 flex flex-col gap-0.5  border border-dashed relative cursor-pointer ${
            rest?.className || ''
          }`
        })}
      >
        <input {...getInputProps()} />
        <FontAwesomeIcon icon={['fas', 'cloud-upload-alt']} size='3x' className='text-primary my-2' />
        <p className='text-primary text-3xl text-center'>{title}</p>
        <p className='text-muted-foreground text-center text-sm'>
          Supported files: <b className='text-primary'>{supportedFormatsDescription ?? (formats || 'All')}</b>
        </p>
        {maxSize > 0 && (
          <p className='text-muted-foreground text-center text-sm'>
            Maximum file size:{' '}
            <b className='text-secondary'>
              {maxFileSize} {fileSizeUnit}
            </b>{' '}
            per file
          </p>
        )}

        <div className='absolute bottom-0 left-0 right-0'>
          <p className='text-muted-foreground opacity-75 py-2 px-2 bg-muted  text-center text-sm '>
            Drag and drop files here, or click to select files
          </p>
          {maxFiles ? (
            <p className='text-muted-foreground opacity-75 py-2 bg-muted text-center text-sm '>
              ({maxFiles} {maxFiles > 1 ? 'files are' : 'file is'} the maximum number of files you can drop
              here)
            </p>
          ) : (
            <p className='text-muted-foreground opacity-75 py-2 bg-muted text-center text-sm '>
              (No limit on the number of files you can drop here)
            </p>
          )}
        </div>
      </div>
    </>
  );
};

export default FileDropZone;
